import AnalyseBill from "assets/print-templates/analyse-bill.jpg";
import { numberFormatAsString } from "commons/ultilities";
import { dateTimeDisplay } from "commons/ultilities/date/dayjs";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useMemo } from "react";
import { ElementToPrint } from "share-components/templates/print/element";
import { PreviewPrinting } from "share-components/templates/print/print-preview";
export const AnalysisBillPreview = observer(() => {
  const {
    productManagement: { openPrintPreview, setProp, productDetail },
  } = useStores();
  const onCloseHandler = useCallback(() => {
    setProp("openPrintPreview", false);
  }, [setProp]);

  const dateElements = useMemo(() => {
    const now = dayjs();
    const dateTime: ElementToPrint[] = [
      {
        name: "day",
        left: "12.5cm",
        top: "2.6cm",
        value: now.date().toString(),
      },
      {
        name: "month",
        left: "14.5cm",
        top: "2.6cm",
        value: (now.month() + 1).toString(),
      },
      {
        name: "year",
        left: "16.2cm",
        top: "2.6cm",
        value: now.year().toString(),
      },
    ];
    const qcPrefixNumber = `PKN${now.year().toString().slice(-2)}/`;
    const qcNumber: ElementToPrint[] = [
      {
        left: "14.2cm",
        top: "3.3cm",
        name: "qcNumber",
        value: qcPrefixNumber,
      },
    ];
    return [...dateTime, ...qcNumber];
  }, []);

  const paperTypeValue = useMemo(() => {
    const value = `${productDetail?.material?.paperType || ""} ${
      productDetail?.material?.gsmStandard?.toString() || ""
    }`;

    const gsmRange = `${productDetail?.material?.gsmLower || ""} ~ ${
      productDetail?.material?.gsmUpper || ""
    }`;

    return `${value} ${gsmRange.trim() === "~" ? "" : `( ${gsmRange} )`}`;
  }, [
    productDetail?.material?.gsmLower,
    productDetail?.material?.gsmStandard,
    productDetail?.material?.gsmUpper,
    productDetail?.material?.paperType,
  ]);

  const weightValue = useMemo(() => {
    const value =
      productDetail?.dimensionWeight?.weightStandard?.toString() || "";

    const weightRange = `${
      productDetail?.dimensionWeight?.weightLower || ""
    } ~ ${productDetail?.dimensionWeight?.weightUpper || ""}`;

    return `${value} ${weightRange.trim() === "~" ? "" : `( ${weightRange} )`}`;
  }, [
    productDetail?.dimensionWeight?.weightLower,
    productDetail?.dimensionWeight?.weightStandard,
    productDetail?.dimensionWeight?.weightUpper,
  ]);

  const thicknessValue = useMemo(() => {
    const value = productDetail?.material?.thicknessStandard?.toString() || "";

    const thickRange = `${productDetail?.material?.thicknessLower || ""} ~ ${
      productDetail?.material?.thicknessUpper || ""
    }`;

    return `${value} ${thickRange.trim() === "~" ? "" : `( ${thickRange} )`}`;
  }, [
    productDetail?.material?.thicknessLower,
    productDetail?.material?.thicknessStandard,
    productDetail?.material?.thicknessUpper,
  ]);

  const productElements = useMemo(() => {
    const products: ElementToPrint[] = [
      {
        name: "customer",
        value: productDetail?.customer?.name || "",
        top: "4.4cm",
        left: "4.8cm",
      },
      {
        name: "name",
        value: productDetail?.name || "",
        top: "5.2cm",
        left: "3.2cm",
      },
      {
        name: "code",
        value: productDetail?.code || "",
        top: "6cm",
        left: "3.2cm",
      },
      {
        name: "paperType",
        value: paperTypeValue,
        top: "11cm",
        left: "5.7cm",
      },
      {
        name: "gsmStandardDisparity",
        value:
          productDetail?.material?.paperType ||
          productDetail?.material?.gsmStandard
            ? "05"
            : "",
        top: "11cm",
        left: "11.5cm",
      },
      {
        name: "thicknessStandard",
        value: thicknessValue || "",
        top: "11.7cm",
        left: "5.7cm",
      },
      {
        name: "thicknessStandardDisparity",
        value: productDetail?.material?.thicknessStandard ? `05` : ``,
        top: "11.7cm",
        left: "11.5cm",
      },
      {
        name: "weightStandard",
        value: weightValue,
        top: "12.5cm",
        left: "5.7cm",
      },
      {
        name: "weightStandardDisparity",
        value: productDetail?.dimensionWeight?.weightStandard ? `05` : ``,
        top: "12.5cm",
        left: "11.5cm",
      },
      {
        name: "lengthStandard",
        value:
          numberFormatAsString(
            productDetail?.dimensionWeight?.lengthStandard?.toString() || "",
            { emtyWhenZero: true }
          ) || "",
        top: "13.7cm",
        left: "5.7cm",
      },
      {
        name: "widthStandard",
        value:
          numberFormatAsString(
            productDetail?.dimensionWeight?.widthStandard?.toString() || "",
            { emtyWhenZero: true }
          ) || "",
        top: "14.6cm",
        left: "5.7cm",
      },
      {
        name: "heightStandard",
        value:
          numberFormatAsString(
            productDetail?.dimensionWeight?.heightStandard?.toString() || "",
            { emtyWhenZero: true }
          ) || "",
        top: "15.4cm",
        left: "5.7cm",
      },
      {
        name: "lengthStandardDisparity",
        value: productDetail?.dimensionWeight?.lengthStandard ? "01" : "",
        top: "13.7cm",
        left: "11.2cm",
      },
      {
        name: "widthStandardDisparity",
        value: productDetail?.dimensionWeight?.widthStandard ? "01" : "",
        top: "14.6cm",
        left: "11.2cm",
      },
      {
        name: "heightStandardDisparity",
        value: productDetail?.dimensionWeight?.heightStandard ? "01" : "",
        top: "15.4cm",
        left: "11.2cm",
      },
      {
        name: "approval",
        value: `Theo ký duyệt ${dateTimeDisplay(
          productDetail?.approval?.businessDate
        )}`,
        top: "16.6cm",
        left: "5.7cm",
      },
      {
        name: "frontPrintColorCode",
        value: `${productDetail?.process?.frontPrintColorCode || ""} ${
          productDetail?.process?.backPrintColorCode || ""
        }`,
        top: "17.5cm",
        left: "5.7cm",
      },
      {
        name: "standardPrint",
        value: productDetail?.process?.printStandard || "",
        top: "18.3cm",
        left: "5.7cm",
      },
      {
        name: "frontPrintMatte",
        value: productDetail?.process?.frontPrintMatte || "",
        top: "19cm",
        left: "5.7cm",
      },
      {
        name: "shape",
        value: productDetail?.process?.shape || "",
        top: "19.8cm",
        left: "5.7cm",
      },
      {
        name: "paste",
        value: productDetail?.process?.paste || "",
        top: "20.6cm",
        left: "5.7cm",
      },
      {
        name: "package",
        value: productDetail?.process?.package || "",
        top: "21.3cm",
        left: "5.7cm",
      },
    ];
    return products;
  }, [
    paperTypeValue,
    productDetail?.approval?.businessDate,
    productDetail?.code,
    productDetail?.customer?.name,
    productDetail?.dimensionWeight?.heightStandard,
    productDetail?.dimensionWeight?.lengthStandard,
    productDetail?.dimensionWeight?.weightStandard,
    productDetail?.dimensionWeight?.widthStandard,
    productDetail?.material?.gsmStandard,
    productDetail?.material?.paperType,
    productDetail?.material?.thicknessStandard,
    productDetail?.name,
    productDetail?.process?.backPrintColorCode,
    productDetail?.process?.frontPrintColorCode,
    productDetail?.process?.frontPrintMatte,
    productDetail?.process?.package,
    productDetail?.process?.paste,
    productDetail?.process?.printStandard,
    productDetail?.process?.shape,
    thicknessValue,
    weightValue,
  ]);

  const elements = useMemo(() => {
    return [...dateElements, ...productElements];
  }, [dateElements, productElements]);
  return (
    <PreviewPrinting
      open={openPrintPreview}
      elements={elements}
      templateImage={AnalyseBill}
      fontSize="13px"
      paperSize={{
        height: "28.8cm",
        width: "20.5cm",
      }}
      onClose={onCloseHandler}
    />
  );
});
